import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getContrastTextColor } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  User,
  Mail,
  Loader2,
  Crown,
  Bell,
  Shield,
  Briefcase,
  CheckCircle,
  Settings,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  // Access Permissions
  office_access: { label: 'Office Dashboard Access', description: 'Access the Office Dashboard for expense/timesheet approvals and payments' },
  view_financials: { label: 'View Financials', description: 'See pricing, costs, and financial data in proposals and jobs' },
  view_reports: { label: 'View Reports', description: 'Access the Reports page and analytics' },
  
  // Job Permissions
  create_jobs: { label: 'Create Jobs', description: 'Create new jobs' },
  edit_jobs: { label: 'Edit Jobs', description: 'Edit existing jobs (add notes, photos, files)' },
  delete_jobs: { label: 'Delete Jobs', description: 'Delete jobs permanently' },
  assign_jobs: { label: 'Assign Jobs', description: 'Assign team members to jobs' },
  
  // Approval Permissions
  can_approve_field_reports: { label: 'Approve Field Reports', description: 'Approve PreCon, SoftDig, and Locate reports' },
  can_receive_approval_notifications: { label: 'Receive Approval Notifications', description: 'Get an email + in-app notification when a field report is approved, so you can package the PDF and deliver to the customer' },
  can_approve_equipment_forms: { label: 'Approve Equipment Forms', description: 'Approve equipment check-in/check-out forms' },
  can_receive_expense_notifications: { label: 'Receive Expense Report Notifications', description: 'Get an email + in-app notification when a team member submits an expense report for approval' },
  
  // Management Permissions
  manage_team: { label: 'Manage Team', description: 'Add and edit team members' },
  delete_team_members: { label: 'Delete Team Members', description: 'Remove team members' },
  manage_services: { label: 'Manage Services', description: 'Add, edit, and delete services' },
  manage_contacts: { label: 'Manage Contacts', description: 'Add, edit, and delete contacts' },
  manage_equipment: { label: 'Manage Equipment', description: 'Add, edit, and delete equipment' },
  manage_calendar_events: { label: 'Manage Calendar Events', description: 'Create, edit, and delete standalone calendar events (PTO, meetings, training, etc.)' },
  
  // Proposal Permissions
  view_proposals: { label: 'View Proposals', description: 'View proposals (read-only)' },
  create_proposals: { label: 'Create/Edit Proposals', description: 'Create and edit proposals' }
};

const ALERT_PERMISSION_LABELS = {
  alert_urgent_jobs: { label: 'Urgent Jobs Alert', description: 'Show urgent jobs banner on dashboard' },
  alert_pending_jobs: { label: 'Pending Jobs Alert', description: 'Show pending jobs banner on dashboard' },
  alert_811_reminders: { label: '811 Reminders Alert', description: 'Show 811 ticket reminders on dashboard' },
  alert_assigned_jobs: { label: 'Assigned Jobs Alert', description: 'Show alert when assigned to a new job' }
};

export default function Permissions() {
  const { isAdmin, isSuperUser, user, refreshUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expandedMembers, setExpandedMembers] = useState(new Set());

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      // Filter out super_users - they have all permissions by default
      // Include admins and managers so we can grant them office_access
      setMembers(response.data.filter(m => m.role !== 'super_user'));
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-admins
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  // Toggle expand/collapse for a single member
  const toggleMember = (memberId) => {
    setExpandedMembers(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  // Expand or collapse all members
  const toggleAll = (expand) => {
    if (expand) {
      setExpandedMembers(new Set(members.map(m => m.id)));
    } else {
      setExpandedMembers(new Set());
    }
  };

  // Count enabled permissions for a member
  const countEnabledPermissions = (member) => {
    const allKeys = [
      ...Object.keys(PERMISSION_LABELS),
      ...Object.keys(ALERT_PERMISSION_LABELS)
    ];
    const enabled = allKeys.filter(key => {
      // Alert permissions default to true
      if (key.startsWith('alert_')) {
        return member.permissions?.[key] !== false;
      }
      return member.permissions?.[key] === true;
    });
    return { enabled: enabled.length, total: allKeys.length };
  };

  const handleUpgradeMember = async (memberId, memberName) => {
    if (!user?.is_owner) {
      toast.error('Only the account owner can promote users to Super User');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to upgrade ${memberName} to Super User? This will give them full access to all features.`)) {
      return;
    }
    
    setSaving(prev => ({ ...prev, [memberId]: true }));
    try {
      await axios.post(`${API}/team/${memberId}/make-super-user`);
      toast.success(`${memberName} is now a Super User!`);
      fetchTeam(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade');
    } finally {
      setSaving(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handlePermissionChange = async (memberId, permission, value) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    const newPermissions = {
      ...member.permissions,
      [permission]: value
    };

    setSaving(prev => ({ ...prev, [memberId]: true }));
    try {
      await axios.put(`${API}/team/${memberId}/permissions`, newPermissions);
      
      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId 
          ? { ...m, permissions: newPermissions }
          : m
      ));
      
      toast.success('Permission updated');
    } catch (error) {
      toast.error('Failed to update permission');
    } finally {
      setSaving(prev => ({ ...prev, [memberId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-64 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Permissions
          </h1>
          <p className="text-slate-500 mt-1">Manage what team members can do in the app</p>
        </div>
        {members.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleAll(true)}
              className="text-slate-600"
              data-testid="expand-all-btn"
            >
              <ChevronsUpDown className="w-4 h-4 mr-1" />
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleAll(false)}
              className="text-slate-600"
              data-testid="collapse-all-btn"
            >
              <ChevronUp className="w-4 h-4 mr-1" />
              Collapse All
            </Button>
          </div>
        )}
      </div>

      {/* Claim Ownership Banner - Only show for super_users who aren't already owners */}
      {user?.role === 'super_user' && !user?.is_owner && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Crown className="w-6 h-6 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Claim Account Ownership</p>
                  <p className="text-sm text-amber-700">
                    As a Super User, you can claim ownership of this account. The owner is the only person who can promote others to Super User.
                  </p>
                </div>
              </div>
              <Button 
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to claim ownership? This can only be done once.')) return;
                  try {
                    await axios.post(`${API}/team/claim-ownership`);
                    toast.success('You are now the account owner!');
                    if (refreshUser) await refreshUser();
                    window.location.reload();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to claim ownership');
                  }
                }}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold whitespace-nowrap"
              >
                <Crown className="w-4 h-4 mr-2" />
                Claim Ownership
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members Permissions */}
      {members.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No team members</h3>
            <p className="text-slate-500">Invite team members to manage their permissions</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {members.map(member => {
            const isExpanded = expandedMembers.has(member.id);
            const permCount = countEnabledPermissions(member);
            
            return (
            <Card key={member.id} className="border-slate-200" data-testid={`permission-card-${member.id}`}>
              {/* Collapsible Header - Always visible */}
              <div 
                className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'border-b border-slate-100' : ''}`}
                onClick={() => toggleMember(member.id)}
                data-testid={`permission-toggle-${member.id}`}
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-base font-bold shadow-sm flex-shrink-0"
                  style={{ backgroundColor: member.calendar_color || '#f59e0b', color: getContrastTextColor(member.calendar_color || '#f59e0b') }}
                >
                  {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{member.full_name}</span>
                    <Badge variant="outline" className="capitalize text-xs">{member.role}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{member.email}</span>
                  </div>
                </div>
                {/* Permission summary badge when collapsed */}
                {!isExpanded && (
                  <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">
                    {permCount.enabled} of {permCount.total} enabled
                  </Badge>
                )}
                {/* Only Owner can promote Admins to Super User */}
                {member.role === 'admin' && user?.is_owner && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpgradeMember(member.id, member.full_name);
                    }}
                    disabled={saving[member.id]}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    data-testid={`upgrade-${member.id}-btn`}
                  >
                    {saving[member.id] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Crown className="w-3 h-3 mr-1" />
                    )}
                    Make Super
                  </Button>
                )}
                <div className="flex-shrink-0 text-slate-400">
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>
              
              {/* Collapsible Content - Permissions */}
              {isExpanded && (
              <CardContent className="pt-4">
                <div className="space-y-6">
                  {/* Access Permissions */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Access Permissions
                    </h4>
                    <div className="space-y-3 pl-2 border-l-2 border-slate-200">
                      {['office_access', 'view_financials', 'view_reports'].map(key => {
                        const { label, description } = PERMISSION_LABELS[key];
                        return (
                          <div key={key} className="flex items-center justify-between py-2">
                            <div>
                              <Label className="font-medium">{label}</Label>
                              <p className="text-xs text-slate-500">{description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {saving[member.id] && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                              <Switch
                                checked={member.permissions?.[key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                                disabled={saving[member.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Job Permissions */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> Job Permissions
                    </h4>
                    <div className="space-y-3 pl-2 border-l-2 border-blue-200">
                      {['create_jobs', 'edit_jobs', 'delete_jobs', 'assign_jobs'].map(key => {
                        const { label, description } = PERMISSION_LABELS[key];
                        return (
                          <div key={key} className="flex items-center justify-between py-2">
                            <div>
                              <Label className="font-medium">{label}</Label>
                              <p className="text-xs text-slate-500">{description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {saving[member.id] && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                              <Switch
                                checked={member.permissions?.[key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                                disabled={saving[member.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Approval Permissions */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Approval Permissions
                    </h4>
                    <div className="space-y-3 pl-2 border-l-2 border-green-200">
                      {['can_approve_field_reports', 'can_receive_approval_notifications', 'can_approve_equipment_forms'].map(key => {
                        const { label, description } = PERMISSION_LABELS[key];
                        return (
                          <div key={key} className="flex items-center justify-between py-2">
                            <div>
                              <Label className="font-medium">{label}</Label>
                              <p className="text-xs text-slate-500">{description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {saving[member.id] && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                              <Switch
                                checked={member.permissions?.[key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                                disabled={saving[member.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Management Permissions */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Management Permissions
                    </h4>
                    <div className="space-y-3 pl-2 border-l-2 border-amber-200">
                      {['manage_team', 'delete_team_members', 'manage_services', 'manage_contacts', 'manage_equipment', 'manage_calendar_events'].map(key => {
                        const { label, description } = PERMISSION_LABELS[key];
                        return (
                          <div key={key} className="flex items-center justify-between py-2">
                            <div>
                              <Label className="font-medium">{label}</Label>
                              <p className="text-xs text-slate-500">{description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {saving[member.id] && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                              <Switch
                                checked={member.permissions?.[key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                                disabled={saving[member.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Proposal Permissions */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Proposal Permissions
                    </h4>
                    <div className="space-y-3 pl-2 border-l-2 border-purple-200">
                      {['view_proposals', 'create_proposals'].map(key => {
                        const { label, description } = PERMISSION_LABELS[key];
                        return (
                          <div key={key} className="flex items-center justify-between py-2">
                            <div>
                              <Label className="font-medium">{label}</Label>
                              <p className="text-xs text-slate-500">{description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {saving[member.id] && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                              <Switch
                                checked={member.permissions?.[key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                                disabled={saving[member.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Dashboard Alerts Section */}
                <div className="pt-4 mt-4 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2 mb-4">
                    <Bell className="w-4 h-4" />
                    Dashboard Alerts
                  </h4>
                  {Object.entries(ALERT_PERMISSION_LABELS).map(([key, { label, description }]) => (
                    <div 
                      key={key}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                    >
                      <div>
                        <Label className="font-medium">{label}</Label>
                        <p className="text-sm text-slate-500">{description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {saving[member.id] && (
                          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                        )}
                        <Switch
                          checked={member.permissions?.[key] !== false}
                          onCheckedChange={(checked) => handlePermissionChange(member.id, key, checked)}
                          disabled={saving[member.id]}
                          data-testid={`alert-${member.id}-${key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
